import { ChatIcon, CloseIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Stack,
  StackDivider,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {
  AppName,
  AssessmentInterface,
  ASSESSMENTS_ADMIN,
  AssessmentSnippet,
  AssessmentStage,
  CollectionFilter,
  COLOR_TAG_COLORS,
  colors,
  ColorTagColor,
  formatDollarValue,
  getDateString,
  getLogInfo,
  getPracticeAssessmentCollectionPath,
  getReverseName,
  getTotalFromInsurer,
  getTotalFromPatient,
  LogEntry,
  PracticeAssessmentData,
  PracticeAssessmentStage,
  User,
  USER_INVITES,
  USER_SETTINGS,
  USERS,
  UserSettings,
  WithId,
} from '@hb/shared'
import { doc, DocumentReference, setDoc } from 'firebase/firestore'

import {
  BaseColumnRenderProps,
  DataColumn,
  DataColumns,
  useAuth,
  usePracticeAccess,
} from '@hb/react'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { db } from '../../../backend/db/db'
import { useDocument } from '../../../backend/db/hooks'
import { saveColorTag } from '../../../backend/functions/gyn'
import { optionalPracticeSelectField } from '../../../collections/fields'
import { useDataListContext } from '../../../contexts/DataListContext'
import { useAssessmentLabResults } from '../../../hooks/backend/user/useAssessmentLabResults'
import { useAssessmentProviderFlags } from '../../../hooks/backend/user/useAssessmentProviderFlags'
import { useUpdateDoc } from '../../../hooks/backend/useUpdateDoc'
import { GrayFlaskIcon } from '../../../icons/FlaskIcon'
import { ProfilePicturePopover } from '../../Admins/ProfilePicturePopover'
import { ActionButton } from '../../Buttons/ActionButton'
import { DisplayButtonBase } from '../../Buttons/DisplayButton'
import { EllipsisText } from '../../EllipsisText'
import { getStatusButton } from '../../Status/hooks'
import { LogEntryPopover, LogEntryView } from '../../Users/Profile/Log/LogEntry'
import { monthDivider } from '../dividers/monthDivider'
import { FieldSelectHeader } from '../PresetFilters'
import { ColumnHeaderText, SortButton } from '../SortButton'
import {
  getAssignedAdminsColumn,
  getDeliveredOnColumn,
  getOverduePaymentColumn,
  getRenderPracticeAssessmentNextAction,
  getUnreadMessagesColumn,
  insurerColumn,
  RenderAssessmentNextAction,
} from './users/shared'
import { AdminAssessmentColumn, AssessmentColumn, PracticeAssessmentColumn } from './users/types'

const providerColumn: AdminAssessmentColumn = {
  title: 'Provider',
  // Header: () => <SortButton sortKey="midwifeName">PROVIDER</SortButton>,
  Header: () => (
    <FieldSelectHeader
      propName="midwifeId"
      sortPropName="midwifeName"
      defaultDir="asc"
      label="PROVIDER"
      field={optionalPracticeSelectField}
    />
  ),
  sortKey: 'midwifeName',
  Render: ({ data }) => <EllipsisText text={data?.midwifeName || 'None'} withTooltip />,
}

const getAdminNameColor = (tabName: string, snippet: AssessmentSnippet) => {
  const { inquiryRank, signedOnByPractice } = snippet ?? {}
  if (tabName !== 'Inquiry' && tabName !== 'All Assessments') return ['#2D3748', '#2D3748']

  const onInquiryAndSignedOn = snippet.stages?.includes('Inquiry') && signedOnByPractice
  switch (inquiryRank) {
    case 1:
    case 2:
    case 3:
      return ['green.500', onInquiryAndSignedOn ? 'red.500' : 'green.500']
    case 4:
      return ['yellow.500', 'yellow.500']
    // signed on by practice
    // case 5:
    default:
      return onInquiryAndSignedOn ? ['red.500', 'red.500'] : ['#2D3748', '#2D3748']
    // return ['#2D3748', '#2D3748']
  }
}

const getAdminNameDarkColor = (tabName: string, snippet: AssessmentSnippet) => {
  const { inquiryRank, signedOnByPractice } = snippet ?? {}
  if (tabName !== 'Inquiry' && tabName !== 'All Assessments') return ['gray.100', 'gray.100']

  const onInquiryAndSignedOn = snippet.stages?.includes('Inquiry') && signedOnByPractice
  switch (inquiryRank) {
    case 1:
    case 2:
    case 3:
      return ['green.500', onInquiryAndSignedOn ? 'red.500' : 'green.500']
    case 4:
      return ['yellow.500', 'yellow.500']
    // signed on by practice
    // case 5:
    default:
      return onInquiryAndSignedOn ? ['red.500', 'red.500'] : ['gray.100', 'gray.100']
    // return ['#2D3748', '#2D3748']
  }
}

const nextActionMobile: AdminAssessmentColumn['mobile'] = {
  width: 275,
  Render: props => (
    <Center gap={2} pl={1} borderRadius={4} w="100%" flex={1}>
      <Text
        lineHeight={1}
        fontFamily="Hero-New"
        fontSize="xs"
        whiteSpace="nowrap"
        fontWeight={600}
        color="gray.500">
        NEXT ACTION
      </Text>
      <Flex flex={1} minW="0">
        <RenderAssessmentNextAction {...props} />
      </Flex>
    </Center>
  ),
}

const getPracticeNextActionMobile = (
  practiceId: string,
): DataColumn<PracticeAssessmentData>['mobile'] => ({
  width: 275,
  Render: props => {
    const Render = getRenderPracticeAssessmentNextAction(practiceId)
    return (
      <Center gap={2} pl={1} borderRadius={4} w="100%" flex={1}>
        <Text
          lineHeight={1}
          fontFamily="Hero-New"
          fontSize="xs"
          whiteSpace="nowrap"
          fontWeight={600}
          color="gray.500">
          NEXT ACTION
        </Text>
        <Flex flex={1} minW="0">
          <Render {...props} />
        </Flex>
      </Center>
    )
  },
})

export const assessmentNextActionColumn: AdminAssessmentColumn = {
  title: 'Next Action Date',
  Header: () => (
    <SortButton defaultDir="asc" sortKey="nextActionDate">
      NEXT ACTION
    </SortButton>
  ),
  width: 350,
  isLazy: true,
  sortKey: 'nextActionDate',
  defaultSortDirection: 'asc',
  Render: RenderAssessmentNextAction,
  mobile: nextActionMobile,
}

const getPracticeAssessmentNextActionColumn = (practiceId: string): PracticeAssessmentColumn => ({
  title: 'Next Action Date',
  Header: () => (
    <SortButton defaultDir="asc" sortKey="nextActionDate">
      <Text fontSize="sm" fontFamily="Hero-New">
        NEXT ACTION
      </Text>
    </SortButton>
  ),
  width: 300,
  isLazy: true,
  sortKey: 'nextActionDate',
  defaultSortDirection: 'asc',
  Render: getRenderPracticeAssessmentNextAction(practiceId),
  mobile: getPracticeNextActionMobile(practiceId),
})

const assessmentNeededFilter: CollectionFilter<AssessmentSnippet> = ['inquiryRank', '==', 1]
const signOnFormCompleteFilter: CollectionFilter<AssessmentSnippet> = ['inquiryRank', '==', 2]
const signedOnByPracticeFilter: CollectionFilter<AssessmentSnippet> = ['inquiryRank', '==', 3]
const StatusFilterButton = ({
  filters,
  filter,
  color,
  text,
  onFilterClick,
  onFilterRemove,
}: {
  filters: Array<CollectionFilter<AssessmentSnippet>>
  filter: CollectionFilter<AssessmentSnippet>
  color: string
  text: string
  onFilterClick: () => void
  onFilterRemove: () => void
}) => (
  <HStack>
    <Button onClick={onFilterClick} color={color} variant="link" size="sm">
      {text}
    </Button>
    {filters.includes(filter) ? (
      <Button px={2} bg={color} color="white" variant="link" size="sm" onClick={onFilterRemove}>
        FILTER
        <CloseIcon width={3} ml={2} />
      </Button>
    ) : null}
  </HStack>
)

const renderNameColumn =
  (isMobile: boolean) =>
  ({ data, app, tabName }: BaseColumnRenderProps<AssessmentSnippet>) => {
    const { id, status, isGynProfile, assessmentName, partnerName } = data ?? {}
    const { appName } = app
    const { buttonText, buttonColor } = getStatusButton('assessment', status || 'incomplete')
    const color = getAdminNameColor(tabName, data)
    const darkColor = getAdminNameDarkColor(tabName, data)
    const showPregnancyName = assessmentName && !assessmentName.trim().startsWith('EDD:')
    const [firstNamePart, secondNamePart] = getReverseName(data).split(', ')
    return (
      <Flex w="100%" align="center" gap={2}>
        <ProfilePicturePopover userId={data?.patientId} size={8} noPopover noOnlineIndicator />
        <Link
          style={{ opacity: 1, flex: 1, minWidth: 0 }}
          to={`${
            appName === 'app' ? '/admin' : ''
          }/pregnancies/${id}${tabName === 'Claim' ? '/claims' : '/pregnancy'}`}>
          <Flex position="relative" bottom={showPregnancyName ? '2px' : '0px'} flexFlow="column">
            <Flex>
              <Tooltip openDelay={200} placement="top" hasArrow bg={buttonColor} label={buttonText}>
                <Button
                  fontWeight={500}
                  justifyContent="flex-start"
                  fontSize="md"
                  minW="0"
                  alignItems="center"
                  variant="link"
                  textDecoration={isMobile ? 'underline' : 'none'}>
                  <Text
                    style={{ textDecoration: isMobile ? 'underline' : 'none' }}
                    textAlign="left"
                    isTruncated>
                    <Text as="span" color={color[0]} _dark={{ color: darkColor[0] }}>
                      {`${firstNamePart ?? 'Missing last'}, `}
                    </Text>
                    <Text as="span" color={color[1]} _dark={{ color: darkColor[1] }}>
                      {secondNamePart ?? 'Missing first'}
                    </Text>
                  </Text>
                  {isGynProfile ? (
                    <Badge ml={1.5} colorScheme="pink" variant="subtle">
                      GYN
                    </Badge>
                  ) : null}
                </Button>
              </Tooltip>
            </Flex>
            {showPregnancyName ? (
              <Text
                fontSize="xs"
                lineHeight={1}
                fontWeight={500}
                pb={1}
                opacity={0.7}
                textAlign="left"
                isTruncated>
                {assessmentName}
              </Text>
            ) : null}
          </Flex>
        </Link>
        {partnerName ? (
          <Tooltip
            placement="top"
            hasArrow
            openDelay={200}
            bg="gray.500"
            label={`Partner: ${partnerName}`}>
            <IconButton
              size="sm"
              variant="ghost"
              borderRadius="full"
              aria-label="Partner"
              icon={<Image src="/images/svg/family.svg" width="20px" opacity={0.5} />}
            />
          </Tooltip>
        ) : null}
      </Flex>
    )
  }

export const nameStatusColumn: AdminAssessmentColumn = {
  title: 'Patient Name / Status',
  Header: () => {
    const { filterBy, userFilters, removeFilter } = useDataListContext<AssessmentSnippet>()
    return (
      <Flex w="100%" pr={2} justify="space-between" align="center">
        <SortButton sortKey="lname">NAME</SortButton>
        <SortButton defaultDir="asc" sortKey="inquiryRank">
          STATUS
        </SortButton>
        <Popover isLazy trigger="hover">
          <PopoverTrigger>
            <ActionButton
              _hover={{ bg: `${colors.green.hex}88` }}
              bg={colors.green.hex}
              border="none"
              color="white"
              px={2}
              lineHeight="12px"
              size="xs"
              colorScheme="blue">
              KEY
            </ActionButton>
          </PopoverTrigger>
          <PopoverContent w="auto">
            <PopoverArrow />
            <Stack spacing={1} p={2}>
              <StatusFilterButton
                color="green.500"
                filter={assessmentNeededFilter}
                filters={userFilters}
                text="Questionnaire submitted, assessment needed"
                onFilterClick={() => filterBy(assessmentNeededFilter)}
                onFilterRemove={() => removeFilter('inquiryRank')}
              />
              <StatusFilterButton
                color="yellow.500"
                filter={signOnFormCompleteFilter}
                filters={userFilters}
                text="Sign on form complete, but not signed on by practice"
                onFilterClick={() => filterBy(signOnFormCompleteFilter)}
                onFilterRemove={() => removeFilter('inquiryRank')}
              />
              <StatusFilterButton
                color="red.500"
                filter={signedOnByPracticeFilter}
                filters={userFilters}
                text="Signed on by practice, but not on auth list"
                onFilterClick={() => filterBy(signedOnByPracticeFilter)}
                onFilterRemove={() => removeFilter('inquiryRank')}
              />
            </Stack>
          </PopoverContent>
        </Popover>
      </Flex>
    )
  },
  width: 280,
  sortKey: 'lname',
  Render: renderNameColumn(false),
  mobile: { width: 220, Render: renderNameColumn(true) },
}

const FlagListPopoverContent = ({
  assessmentId,
  patientId,
  patientIsInvite,
}: {
  assessmentId: string
  patientId: string
  patientIsInvite: boolean
}) => {
  const patientRef = useMemo(
    () => doc(db, patientIsInvite ? USER_INVITES : USERS, patientId) as DocumentReference<User>,
    [patientId, patientIsInvite],
  )
  const { dataArr: flags, loading } = useAssessmentProviderFlags(patientRef, assessmentId)
  const activeFlags = useMemo(() => flags?.filter(f => !f.removedAt) ?? [], [flags])
  return loading ? (
    <Spinner size="xs" />
  ) : activeFlags.length === 0 ? (
    <Text fontSize="xs" color="gray.500">
      No active flags
    </Text>
  ) : (
    <VStack align="flex-start" spacing={1}>
      {activeFlags.map(f => (
        <Text key={f.id} fontSize="xs" color="red.600" lineHeight={1.4}>
          {f.text}
        </Text>
      ))}
    </VStack>
  )
}

// §0.5.2 Fix 1: Red ! popover — lists all active provider flag texts on hover/tap.
const FlagListPopover = ({
  assessmentId,
  patientId,
  patientIsInvite,
}: {
  assessmentId: string
  patientId: string
  patientIsInvite: boolean
}) => (
  <Popover trigger="hover" isLazy placement="top">
    <PopoverTrigger>
      <Text
        fontSize="xs"
        color="red.600"
        fontWeight={700}
        lineHeight={1}
        flexShrink={0}
        cursor="default"
        userSelect="none">
        !
      </Text>
    </PopoverTrigger>
    <PopoverContent w="auto" maxW="280px" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <PopoverArrow />
      <PopoverBody p={2}>
        <FlagListPopoverContent
          assessmentId={assessmentId}
          patientId={patientId}
          patientIsInvite={patientIsInvite}
        />
      </PopoverBody>
    </PopoverContent>
  </Popover>
)

const FLASK_DISMISSAL_TTL_MS = 7 * 24 * 60 * 60 * 1000

// §0.5.2 Fix 2: Gray flask popover — shows overdue lab details on hover/tap and provides dismissal UI.
// Reads labFlaskDismissals from user-settings to suppress labs dismissed within the last 7 days.
const FlaskPopover = ({ assessmentId, patientId }: { assessmentId: string; patientId: string }) => {
  const { authUser } = useAuth()
  const uid = authUser?.uid
  const { dataArr: allLabs, loading: labsLoading } = useAssessmentLabResults(
    patientId,
    assessmentId,
  )
  const { data: userSettings, loading: settingsLoading } = useDocument<UserSettings>(
    USER_SETTINGS,
    uid,
  )
  const [dismissing, setDismissing] = useState<string | null>(null)

  const overdueLabs = useMemo(() => {
    if (!allLabs) return []
    const cutoffMs = Date.now() - FLASK_DISMISSAL_TTL_MS
    return allLabs.filter(l => {
      if (l.resultDate) return false
      if (!l.collectedDate) return false
      return new Date(l.collectedDate).getTime() < cutoffMs
    })
  }, [allLabs])

  // Filter out labs dismissed within the last 7 days; re-include when TTL has expired.
  const visibleLabs = useMemo(() => {
    const dismissals = userSettings?.labFlaskDismissals
    if (!dismissals) return overdueLabs
    const now = Date.now()
    return overdueLabs.filter(lab => {
      const d = dismissals[lab.id]
      if (!d) return true
      return now - d.dismissedAt >= FLASK_DISMISSAL_TTL_MS
    })
  }, [overdueLabs, userSettings])

  const handleDismiss = useCallback(
    async (labId: string, action: 'dismiss' | 'reorder', e: React.MouseEvent) => {
      e.stopPropagation()
      if (!uid || dismissing) return
      setDismissing(labId)
      try {
        await setDoc(
          doc(db, USER_SETTINGS, uid),
          {
            labFlaskDismissals: {
              [labId]: { dismissedAt: Date.now(), action },
            },
          },
          { merge: true },
        )
      } finally {
        setDismissing(null)
      }
    },
    [uid, dismissing],
  )

  const loading = labsLoading || settingsLoading

  // Once loaded, hide the trigger entirely when all labs have been dismissed.
  if (!loading && visibleLabs.length === 0) return null

  return (
    <Popover trigger="hover" isLazy placement="top">
      <PopoverTrigger>
        <Box flexShrink={0} lineHeight={0} cursor="pointer">
          <GrayFlaskIcon size={16} />
        </Box>
      </PopoverTrigger>
      <PopoverContent w="240px" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <PopoverArrow />
        <PopoverBody p={2}>
          {loading ? (
            <Spinner size="xs" />
          ) : (
            <VStack align="flex-start" spacing={2} w="100%">
              {visibleLabs.map(lab => {
                const daysAgo = lab.collectedDate
                  ? Math.floor((Date.now() - new Date(lab.collectedDate).getTime()) / 86_400_000)
                  : null
                return (
                  <Box key={lab.id} w="100%">
                    <Text fontSize="xs" fontWeight={600}>
                      {lab.labName}
                    </Text>
                    {lab.collectedDate ? (
                      <Text fontSize="xs" color="gray.500">
                        Ordered {getDateString(lab.collectedDate, 'short')}
                        {daysAgo !== null ? ` · ${daysAgo}d ago` : ''}
                      </Text>
                    ) : null}
                    <HStack mt={1} spacing={1}>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="gray"
                        isLoading={dismissing === lab.id}
                        onClick={e => handleDismiss(lab.id, 'dismiss', e)}>
                        Dismiss — result pending
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        isLoading={dismissing === lab.id}
                        onClick={e => handleDismiss(lab.id, 'reorder', e)}>
                        Reorder lab
                      </Button>
                    </HStack>
                  </Box>
                )
              })}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

// §0.5.8 Color tag picker — opens a popover for selecting/clearing a provider color tag per patient
export const ColorTagPicker = ({
  assessmentId,
  patientId,
  practiceId,
  appName,
  currentTag,
}: {
  assessmentId: string
  patientId: string
  practiceId: string
  appName: AppName
  currentTag: string | undefined
}) => {
  const [saving, setSaving] = useState(false)

  const handleSelect = useCallback(
    async (color: ColorTagColor | null, e: { stopPropagation: () => void }) => {
      e.stopPropagation()
      if (saving) return
      setSaving(true)
      await saveColorTag({
        assessmentId,
        patientId,
        practiceId,
        appName,
        color,
      })
      setSaving(false)
    },
    [saving, assessmentId, patientId, practiceId, appName],
  )

  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label={currentTag ? `Color tag: ${currentTag}` : 'Set color tag'}
          size="xs"
          variant="ghost"
          isLoading={saving}
          title="Set color tag"
          icon={
            <Box
              w={3}
              h={3}
              borderRadius="full"
              bg={currentTag ? `${currentTag}.400` : 'gray.200'}
              _dark={{ bg: currentTag ? `${currentTag}.400` : 'gray.600' }}
            />
          }
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="auto"
          minW={0}
          p={2}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <PopoverArrow />
          <Flex gap={1.5} align="center">
            {COLOR_TAG_COLORS.map(c => (
              <Box
                key={c}
                as="button"
                w={4}
                h={4}
                borderRadius="full"
                bg={`${c}.400`}
                border="2px solid"
                borderColor={currentTag === c ? `${c}.600` : 'transparent'}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
                onClick={(e: React.MouseEvent) => handleSelect(c, e)}
              />
            ))}
            {currentTag ? (
              <Box
                as="button"
                w={4}
                h={4}
                borderRadius="full"
                bg="gray.200"
                _dark={{ bg: 'gray.600' }}
                cursor="pointer"
                title="Clear tag"
                _hover={{ opacity: 0.8 }}
                onClick={(e: React.MouseEvent) => handleSelect(null, e)}
              />
            ) : null}
          </Flex>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

const renderPracticeSiteNameColumn =
  (isMobile: boolean, onArchivedList: boolean) =>
  ({ data, app: { appName } }: BaseColumnRenderProps<AssessmentInterface>) => {
    // §0.5.2 Patient panel indicators
    const { authUser } = useAuth()
    const { selectedPracticeId } = usePracticeAccess()
    const colorTag = authUser?.uid ? data.colorTagsByProvider?.[authUser.uid] : undefined

    return (
      <Flex
        gap={1.5}
        w="100%"
        align="center"
        h="100%"
        borderLeft="3px solid"
        borderColor={data.deliveredOn ? 'purple.400' : 'transparent'}
        pl={1.5}>
        <ProfilePicturePopover userId={data?.patientId} size={8} noPopover noOnlineIndicator />
        <Link
          style={{ opacity: 1, flex: 1, width: '100%' }}
          to={`${appName === 'app' ? '/admin' : ''}/pregnancies/${data.id}/pregnancy`}>
          <Button
            fontWeight={500}
            justifyContent="flex-start"
            fontSize="md"
            px={1}
            gap={1}
            maxW="100%"
            flex={1}
            minW="0"
            variant="link">
            <Tooltip
              hasArrow
              placement="top"
              bg={data.clearedOn ? colors.green.hex : 'gray.600'}
              label={data.clearedOn ? 'Cleared for coverage' : 'Not cleared for coverage'}>
              <Text
                textAlign="left"
                flex={1}
                color={data.clearedOn ? colors.green.hex : 'gray.600'}
                minW="0"
                textDecor={isMobile ? 'underline' : 'none'}
                isTruncated>
                {data ? getReverseName(data) : 'No name entered'}
              </Text>
            </Tooltip>
            {/* §0.5.2 Unreviewed lab results — red dot (reserved indicator) */}
            {data.hasUnreviewedLabResults ? (
              <Tooltip placement="top" hasArrow bg="red.600" label="Unreviewed lab results">
                <Box w={2} h={2} borderRadius="full" bg="red.500" flexShrink={0} />
              </Tooltip>
            ) : null}
            {data.inLabor ? (
              <Badge colorScheme="red" fontSize="xs" fontFamily="Hero-New" ml={1}>
                In Labor
              </Badge>
            ) : null}
            <ExternalLinkIcon width={3} />
          </Button>
        </Link>
        {/* §0.5.2 Overdue labs — gray flask popover (outside Link to intercept click) */}
        {data.hasOverdueLabs && data.patientId ? (
          <FlaskPopover assessmentId={data.id} patientId={data.patientId} />
        ) : null}
        {/* §0.5.2 Active provider flags — red ! popover listing flag texts (outside Link) */}
        {data.hasActiveProviderFlags ? (
          <FlagListPopover
            patientId={data.patientId}
            patientIsInvite={data.isInvite}
            assessmentId={data.id}
          />
        ) : null}

        {data.partnerName ? (
          <Tooltip
            placement="left"
            hasArrow
            bg="gray.100"
            color="gray.700"
            label={`Partner: ${data.partnerName}`}>
            <IconButton
              size="sm"
              variant="ghost"
              borderRadius="full"
              aria-label="Partner"
              icon={<Image src="/images/svg/family.svg" width="20px" opacity={0.5} />}
            />
          </Tooltip>
        ) : null}
        {data.archived && !onArchivedList ? (
          <Badge colorScheme="red" fontSize="xs" fontFamily="Hero-New" textAlign="center">
            ARCHIVED
          </Badge>
        ) : null}
        {/* §0.5.8 Color tag picker — outside Link so click doesn't navigate */}
        {selectedPracticeId && data.patientId ? (
          <ColorTagPicker
            assessmentId={data.id}
            patientId={data.patientId}
            practiceId={selectedPracticeId}
            appName={appName}
            currentTag={colorTag}
          />
        ) : null}
      </Flex>
    )
  }

const getPracticeNameColumn = (onArchivedList: boolean): AssessmentColumn => ({
  title: 'Patient Name',
  Header: () => <SortButton sortKey="lname">NAME</SortButton>,
  sortKey: 'lname',
  Render: renderPracticeSiteNameColumn(false, onArchivedList),
  mobile: {
    width: 300,
    Render: renderPracticeSiteNameColumn(true, onArchivedList),
  },
})

// export const redFlagsColumn: AdminAssessmentColumn = {
//   title: 'Red Flags',
//   Header: () => {
//     const { sort } = useDataListContext()
//     const isActive = sort.field === 'redFlagText'
//     return (
//       <SortButton noArrow defaultDir="asc" sortKey="redFlagText">
//         <Center w="54px">
//           <RedFlagIcon
//             opacity={isActive ? 1 : 0.5}
//             color={isActive && sort.order === 'asc' ? 'yellow.500' : 'gray.400'}
//           />
//         </Center>
//       </SortButton>
//     )
//   },
//   flexProps: { justify: 'center' },
//   width: 54,
//   Render: ({ data: { redFlagText, id }, cell, preview: { openPreview } }) => (
//     <Flex>
//       <RedFlagIcon
//         onClick={e =>
//           openPreview({
//             id,
//             item: 'redFlags',
//             position: { x: e.clientX, y: e.clientY },
//             cell,
//           })
//         }
//         color={redFlagText && redFlagText !== 'zzz' ? 'yellow.600' : 'gray.400'}
//       />
//     </Flex>
//   ),
// }

// const AssessmentUrgentContent = ({
//   snippet,
//   admins,
//   collection,
// }: {
//   snippet: WithId<AssessmentSnippet>
//   admins: LocalCollectionState<UserRoleItem>
//   collection: string
// }) => {
//   const { id } = snippet ?? {}

//   const docId = useMemo(
//     () => (collection === 'claims' ? (snippet as any)?.assessmentId : id),
//     [collection, id, snippet],
//   )
//   const updateRef = useMemo(
//     () =>
//       docId
//         ? doc(
//             db,
//             ['assessment-snippets', 'claims'].includes(collection)
//               ? 'assessments-admin'
//               : collection,
//             docId,
//           )
//         : null,
//     [collection, docId],
//   )
//   return <UrgentPopoverContent admins={admins} item={snippet} updateRef={updateRef} />
// }

export const eddColumn: AssessmentColumn = {
  title: 'EDD',
  Header: () => (
    <Stack align="center" direction="row" mr={1} px={2} spacing={3} width="100%">
      <Flex align="center" flexGrow={1}>
        <SortButton defaultDir="asc" sortKey="edd">
          EDD
        </SortButton>
      </Flex>
    </Stack>
  ),
  // flexProps: { width: '100%' },
  dividers: [monthDivider],
  width: 100,
  sortKey: 'edd',
  Render: props => (
    <Stack
      direction="row"
      spacing={3}
      pl={1}
      pr={3}
      width="100%"
      justify="flex-start"
      align="center">
      <Text p={1}>{props.data?.edd ? getDateString(props.data.edd, 'short') : 'None'}</Text>
    </Stack>
  ),
  mobile: {
    width: 130,
    Render: ({ data }) => {
      const { edd } = data ?? {}
      return (
        <Center
          py={1}
          boxShadow="1px 1px 3px #00000077"
          gap={1}
          bg="gray.100"
          h="28px"
          borderRadius={4}
          w="100%"
          flex={1}>
          <Text
            lineHeight={1}
            fontFamily="Hero-New"
            fontSize="xs"
            fontWeight={600}
            color="gray.500">
            EDD:
          </Text>
          <Text lineHeight={1} fontFamily="Open Sans" fontSize="sm" color="gray.600">
            {edd ? getDateString(edd, 'short') : 'None'}
          </Text>
        </Center>
      )
    },
  },
}

const RenderAssessmentLogContent = ({
  data,
  isPractice,
  collection,
  id,
}: {
  data: WithId<AssessmentSnippet>
  isPractice?: boolean
  collection: string
  id: string
}) => {
  const log = isPractice ? (data as any).log : data?.logSnippet
  const { lastCreatedKey, lastCreated, lastUpdated, lastUpdatedKey } = getLogInfo(log)

  // const formatted = formatPhoneNumber(phone)
  // if (isScrolling) {
  //   return (
  //     <Center w='100%'>
  //       <Spinner />
  //     </Center>
  //   )
  // }
  const reverseName = getReverseName(data)
  const update = useUpdateDoc()
  const onSave = useCallback(
    async (updated: LogEntry, createdOn?: number) => {
      const docRef = doc(db, collection, id)
      return update(docRef, `log.${createdOn ?? Date.now()}`, updated)
    },
    [update, id, collection],
  )

  return (
    <>
      <PopoverCloseButton />
      <PopoverArrow />
      <Text fontFamily="Hero-New" fontSize="md" fontWeight={600} px={2}>
        {reverseName}
      </Text>
      {log && lastCreatedKey ? (
        <Stack spacing={1} width="349px" divider={<StackDivider />}>
          <Box>
            <Text pt={1} px={2} fontWeight={500} color="gray.600" fontSize="sm">
              Last created
            </Text>
            <LogEntryView
              update={updated => onSave(updated, lastCreatedKey)}
              createdOn={lastCreatedKey}
              text=""
              {...lastCreated}
            />
          </Box>
          {lastUpdatedKey && lastCreatedKey !== lastUpdatedKey ? (
            <Box w="100%" position="relative">
              <Text fontWeight={500} color="gray.600" fontSize="sm">
                Last updated
              </Text>
              <LogEntryView createdOn={lastUpdatedKey} text="" {...lastUpdated} />
            </Box>
          ) : null}
        </Stack>
      ) : (
        <Text w="300px" px={2} color="gray.399">
          No log entries
        </Text>
      )}
      <LogEntryPopover isNew onSubmit={added => onSave(added)}>
        <Button
          aria-label="add log entry"
          ml="auto"
          fontWeight={499}
          size="xs"
          height="auto"
          py={1}
          color="gray.499"
          m={1}
          variant="outline">
          + ADD ENTRY
        </Button>
      </LogEntryPopover>
    </>
  )
}
const getRenderAssessmentLog =
  (
    collection: string,
    isPractice: boolean,
    isMobile: boolean,
  ): FC<BaseColumnRenderProps<WithId<AssessmentSnippet>>> =>
  ({ data }) => {
    const { id } = data ?? {}

    const log = isPractice ? (data as any).log : data?.logSnippet
    const { lastUpdated } = getLogInfo(log)

    const chatIcon = <ChatIcon color={lastUpdated ? 'blue.400' : 'gray.600'} />

    return (
      <Stack flex={1} spacing={1} pr={2} direction="row">
        <Popover closeOnBlur={false} isLazy strategy="fixed">
          <PopoverTrigger>
            <DisplayButtonBase
              size={isMobile ? 'sm' : 'md'}
              aria-label="log"
              icon={
                isMobile ? (
                  <Flex color="gray.600" gap={1} align="center">
                    {chatIcon}
                    <Text fontSize="xs">LOG</Text>
                  </Flex>
                ) : (
                  chatIcon
                )
              }
            />
          </PopoverTrigger>
          <PopoverContent w="auto" px={2} py={2}>
            <PopoverBody>
              <RenderAssessmentLogContent
                data={data}
                isPractice={isPractice}
                collection={collection}
                id={id}
              />
            </PopoverBody>
            <PopoverArrow />
          </PopoverContent>
        </Popover>
      </Stack>
    )
  }

export const assessmentLogColumn: AdminAssessmentColumn = {
  title: 'Log / Contact Info',
  Header: () => <ColumnHeaderText>LOG</ColumnHeaderText>,
  isLazy: true,
  width: 60,
  Render: getRenderAssessmentLog(ASSESSMENTS_ADMIN, false, false),
  mobile: {
    width: 60,
    Render: getRenderAssessmentLog(ASSESSMENTS_ADMIN, false, true),
  },
}

export const getPracticeAssessmentLogColumn = (practiceId: string): AdminAssessmentColumn => ({
  title: 'Log / Contact Info',
  Header: () => <ColumnHeaderText>LOG / CONTACT</ColumnHeaderText>,
  isLazy: true,
  width: 160,
  Render: getRenderAssessmentLog(getPracticeAssessmentCollectionPath(practiceId), true, false),
  mobile: {
    width: 275,
    Render: getRenderAssessmentLog(getPracticeAssessmentCollectionPath(practiceId), true, true),
  },
})

const prmColumn: AssessmentColumn = {
  title: 'PRM',
  Header: () => <ColumnHeaderText>PRM</ColumnHeaderText>,
  Render: ({ data }) => <Text>{data?.prm ? formatDollarValue(data.prm) : 'None'}</Text>,
  width: 100,
  mobile: {
    width: 100,
    Render: ({ data }) => {
      const { prm } = data ?? {}
      return (
        <Flex gap={1} align="center">
          <Text fontWeight={600}>PRM</Text>
          <Text color="gray.700" fontSize="md">
            {formatDollarValue(prm ?? 0)}
          </Text>
        </Flex>
      )
    },
  },
}

const receivedInsurancePaymentsColumn: PracticeAssessmentColumn = {
  title: 'Received Insurance Payments',
  Header: () => <ColumnHeaderText>$ INS</ColumnHeaderText>,
  Render: ({ data }) => {
    const total = getTotalFromInsurer(data)
    return <Text>{formatDollarValue(total ?? 0)}</Text>
  },
  width: 100,
  mobile: {
    width: 170,
    Render: ({ data }) => {
      const total = getTotalFromPatient(data)
      return (
        <Flex gap={2} align="center">
          <Text whiteSpace="nowrap" fontSize="sm" fontWeight={600}>
            From Insurance
          </Text>
          <Text fontSize="md">{formatDollarValue(total ?? 0)}</Text>
        </Flex>
      )
    },
  },
}

const receivedPatientPaymentsColumn: PracticeAssessmentColumn = {
  title: 'Received Patient Payments',
  Header: () => <ColumnHeaderText>$ PATIENT</ColumnHeaderText>,
  width: 100,
  Render: ({ data }) => {
    const total = getTotalFromPatient(data)
    return <Text>{formatDollarValue(total ?? 0)}</Text>
  },
  mobile: {
    width: 170,
    Render: ({ data }) => {
      const total = getTotalFromPatient(data)
      return (
        <Flex gap={2} align="center">
          <Text whiteSpace="nowrap" fontSize="sm" fontWeight={600}>
            From Patient
          </Text>
          <Text fontSize="md">{formatDollarValue(total ?? 0)}</Text>
        </Flex>
      )
    },
  },
}

const balanceTowardPrmColumn: AssessmentColumn = {
  title: 'Balance Toward PRM',
  Header: () => (
    <Box>
      <Tooltip label="Balance Toward PRM" placement="top" hasArrow>
        <ColumnHeaderText>BAL</ColumnHeaderText>
      </Tooltip>
    </Box>
  ),
  Render: ({ data }) => (
    <Text>
      {data?.balanceTowardPrm !== undefined ? formatDollarValue(data.balanceTowardPrm) : 'None'}
    </Text>
  ),
  width: 100,
}

// const signedOnColumn: AssessmentColumn = {
//   title: 'Signed On Date',
//   Header: () => {
//     const { sort } = useDataListContext<AssessmentSnippet>()
//     return (
//       <SortButton noArrow sortKey="signedOnDate">
//         <Tooltip placement="top" hasArrow label="Patient completed sign-on forms">
//           <Image
//             filter={sort.field === 'signedOnDate' ? 'grayscale(0%)' : 'grayscale(100%)'}
//             transition="filter 300ms"
//             height="20px"
//             src={assignmentIcon}
//           />
//         </Tooltip>
//       </SortButton>
//     )
//   },
//   width: 30,
//   sortKey: 'signedOnDate',
//   Render: ({ data }) => {
//     const { signedOnDate } = data ?? {}
//     return (
//       <Center width="100%">
//         <Tooltip
//           placement="top"
//           hasArrow
//           bg={signedOnDate ? colors.green.hex : '#777'}
//           label={
//             signedOnDate
//               ? `Patient completed sign on forms on ${getDateString(signedOnDate, 'short')}`
//               : 'Patient has not completed sign on forms'
//           }>
//           <Image
//             filter={`grayscale(${signedOnDate ? 0 : 100}%)`}
//             height="20px"
//             src={assignmentIcon}
//           />
//         </Tooltip>
//       </Center>
//     )
//   },
//   mobile: {
//     width: 160,
//     Render: ({ data }) => {
//       const { signedOnDate } = data ?? {}
//       return (
//         <Center
//           height="28px"
//           boxShadow="1px 1px 3px #00000077"
//           gap={1}
//           bg={signedOnDate ? colors.green.hex : 'gray.100'}
//           borderRadius={4}
//           w="100%"
//           flex={1}>
//           <Image
//             filter={`brightness(${signedOnDate ? 300 : 100}%)`}
//             height="18px"
//             src={assignmentIcon}
//           />
//           <Text
//             lineHeight={1}
//             fontFamily="Hero-New"
//             fontSize="xs"
//             fontWeight={600}
//             color={signedOnDate ? 'white' : 'gray.500'}>
//             {signedOnDate ? 'SIGNED ON' : 'NOT SIGNED ON'}
//           </Text>
//         </Center>
//       )
//     },
//   },
// }

export const assessmentColumns: DataColumns<AssessmentInterface> = {
  urgent: getAssignedAdminsColumn('assessment') as AssessmentColumn,
  unreadMessages: getUnreadMessagesColumn(null),
  overduePayment: getOverduePaymentColumn(),
  // signedOn: signedOnColumn,
  edd: eddColumn,
  deliveredOn: getDeliveredOnColumn(),
  name: nameStatusColumn as AssessmentColumn,
  // redFlags: redFlagsColumn,
  insurer: insurerColumn,
  midwife: providerColumn as AssessmentColumn,
  nextAction: assessmentNextActionColumn as AssessmentColumn,
  log: assessmentLogColumn as AssessmentColumn,
  // redFlags: redFlagsColumn as AssessmentColumn,
}

export const getProviderAssessmentColumns = (
  stage: PracticeAssessmentStage | AssessmentStage | undefined | null,
  practiceId: string,
  superAdmin: boolean,
  onArchivedList: boolean,
): DataColumns<PracticeAssessmentData> => {
  const columns: DataColumns<PracticeAssessmentData> = superAdmin
    ? {
        unreadMessages: getUnreadMessagesColumn(practiceId),
        // signedOn: signedOnColumn as DataColumn<PracticeAssessmentData>,
        edd: eddColumn as DataColumn<PracticeAssessmentData>,
        deliveredOn: getDeliveredOnColumn(),
        name: getPracticeNameColumn(onArchivedList) as DataColumn<PracticeAssessmentData>,
        // insurer: insurerColumn as AssessmentColumn,
        nextAction: getPracticeAssessmentNextActionColumn(practiceId),
        // log: getPracticeAssessmentLogColumn(practiceId) as AssessmentColumn,
        prm: (stage === 'Complete'
          ? balanceTowardPrmColumn
          : prmColumn) as DataColumn<PracticeAssessmentData>,
        receivedInsurancePayments:
          receivedInsurancePaymentsColumn as DataColumn<PracticeAssessmentData>,
        receivedPatientPayments:
          receivedPatientPaymentsColumn as DataColumn<PracticeAssessmentData>,
        overduePayment: getOverduePaymentColumn(),
      }
    : {
        unreadMessages: getUnreadMessagesColumn(practiceId),
        overduePayment: getOverduePaymentColumn(),
        // signedOn: signedOnColumn as DataColumn<PracticeAssessmentData>,
        edd: eddColumn as DataColumn<PracticeAssessmentData>,
        deliveredOn: getDeliveredOnColumn(),
        name: getPracticeNameColumn(onArchivedList) as DataColumn<PracticeAssessmentData>,
        // insurer: insurerColumn as AssessmentColumn,
        nextAction: getPracticeAssessmentNextActionColumn(practiceId),
        // log: getPracticeAssessmentLogColumn(practiceId) as AssessmentColumn,
      }
  return columns
}
